import React, { Suspense } from 'react';
import { Admin, Resource, houseLightTheme, houseDarkTheme, CustomRoutes } from 'react-admin';
import { Route } from 'react-router-dom';
import dataProvider from './data-providers';
import { TelegramProductShow, TelegramProductEdit, TelegramProductList, TelegramProductCreate } from './telegram-products';
import { authProvider } from './providers/auth-provider';
import { i18nProvider } from './i18n';
import TelegramIcon from '@mui/icons-material/Telegram';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import CategoryIcon from '@mui/icons-material/Category';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PeopleIcon from '@mui/icons-material/People';
import SecurityIcon from '@mui/icons-material/Security';
import StoreIcon from '@mui/icons-material/Store';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import LabelIcon from '@mui/icons-material/Label';
import TuneIcon from '@mui/icons-material/Tune';
import StyleIcon from '@mui/icons-material/Style';
import ContactsIcon from '@mui/icons-material/Contacts';
import CampaignIcon from '@mui/icons-material/Campaign';
import CelebrationIcon from '@mui/icons-material/Celebration';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { CustomLayout } from './layout/CustomLayout';
import { CircularProgress, Box, Typography, Button } from '@mui/material';

const LazyFallback = () => (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
    </Box>
);

class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    { error: Error | null }
> {
    state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    render() {
        if (this.state.error) {
            return (
                <Box p={4} textAlign="center">
                    <Typography variant="h6" color="error" gutterBottom>
                        Something went wrong
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {this.state.error.message}
                    </Typography>
                    <Button variant="outlined" onClick={() => this.setState({ error: null })}>
                        Try again
                    </Button>
                </Box>
            );
        }
        return this.props.children;
    }
}

const lazy = (factory: () => Promise<{ default: React.ComponentType<any> }>) =>
    React.lazy(factory);

const lazyNamed = <T extends string>(
    factory: () => Promise<Record<T, React.ComponentType<any>>>,
    name: T
) => React.lazy(() => factory().then((mod) => ({ default: mod[name] })));

const PromoCodeList = lazy(() => import('./promo-codes/PromoCodeList'));
const PromoCodeCreate = lazy(() => import('./promo-codes/PromoCodeCreate'));
const ProductList = lazyNamed(() => import('./products/ProductList'), 'ProductList');
const ProductShow = lazyNamed(() => import('./products/ProductShow'), 'ProductShow');
const ProductCreate = lazyNamed(() => import('./products/ProductCreate'), 'ProductCreate');
const ProductEdit = lazyNamed(() => import('./products/ProductEdit'), 'ProductEdit');
const CategoryList = lazyNamed(() => import('./categories/CategoryList'), 'CategoryList');
const CategoryShow = lazyNamed(() => import('./categories/CategoryShow'), 'CategoryShow');
const CategoryCreate = lazyNamed(() => import('./categories/CategoryCreate'), 'CategoryCreate');
const CategoryEdit = lazyNamed(() => import('./categories/CategoryEdit'), 'CategoryEdit');
const OrderList = lazyNamed(() => import('./orders/OrdersList'), 'OrderList');
const OrderCreate = lazyNamed(() => import('./orders/OrderCreate'), 'OrderCreate');
const OrderEdit = lazyNamed(() => import('./orders/OrderEdit'), 'OrderEdit');
const OrderShow = lazyNamed(() => import('./orders/OrderShow'), 'OrderShow');
const StockMovementList = lazyNamed(() => import('./stock/StockMovementList'), 'StockMovementList');
const StockMovementCreate = lazyNamed(() => import('./stock/StockMovementCreate'), 'StockMovementCreate');
const MigrationManager = lazyNamed(() => import('./migration/MigrationManager'), 'MigrationManager');
const SettingsPage = lazyNamed(() => import('./app-settings'), 'SettingsPage');
const BouquetBuilderSettingsPage = lazyNamed(() => import('./bouquet-builder'), 'BouquetBuilderSettingsPage');
const Dashboard = lazyNamed(() => import('./dashboard/Dashboard'), 'Dashboard');
const Analytics = lazyNamed(() => import('./analytics/Analytics'), 'Analytics');
const UserList = lazyNamed(() => import('./users'), 'UserList');
const UserCreate = lazyNamed(() => import('./users'), 'UserCreate');
const UserEdit = lazyNamed(() => import('./users'), 'UserEdit');
const UserShow = lazyNamed(() => import('./users'), 'UserShow');
const RoleList = lazyNamed(() => import('./roles'), 'RoleList');
const RoleCreate = lazyNamed(() => import('./roles'), 'RoleCreate');
const RoleEdit = lazyNamed(() => import('./roles'), 'RoleEdit');
const RoleShow = lazyNamed(() => import('./roles'), 'RoleShow');
const PermissionList = lazyNamed(() => import('./roles'), 'PermissionList');
const PermissionCreate = lazyNamed(() => import('./roles'), 'PermissionCreate');
const PermissionEdit = lazyNamed(() => import('./roles'), 'PermissionEdit');
const PermissionShow = lazyNamed(() => import('./roles'), 'PermissionShow');
const RoleManagement = lazyNamed(() => import('./roles'), 'RoleManagement');
const BranchList = lazyNamed(() => import('./branches'), 'BranchList');
const BranchCreate = lazyNamed(() => import('./branches'), 'BranchCreate');
const BranchEdit = lazyNamed(() => import('./branches'), 'BranchEdit');
const BranchShow = lazyNamed(() => import('./branches'), 'BranchShow');
const DeliveryTimeSlotList = lazyNamed(() => import('./delivery-time-slots'), 'DeliveryTimeSlotList');
const DeliveryTimeSlotCreate = lazyNamed(() => import('./delivery-time-slots'), 'DeliveryTimeSlotCreate');
const DeliveryTimeSlotEdit = lazyNamed(() => import('./delivery-time-slots'), 'DeliveryTimeSlotEdit');
const DeliveryTimeSlotShow = lazyNamed(() => import('./delivery-time-slots'), 'DeliveryTimeSlotShow');
const CatalogLabelList = lazyNamed(() => import('./catalog-labels'), 'CatalogLabelList');
const CatalogLabelCreate = lazyNamed(() => import('./catalog-labels'), 'CatalogLabelCreate');
const CatalogLabelEdit = lazyNamed(() => import('./catalog-labels'), 'CatalogLabelEdit');
const CustomizationOptionList = lazyNamed(() => import('./customization-options'), 'CustomizationOptionList');
const CustomizationOptionCreate = lazyNamed(() => import('./customization-options'), 'CustomizationOptionCreate');
const CustomizationOptionEdit = lazyNamed(() => import('./customization-options'), 'CustomizationOptionEdit');
const AttributeDefinitionList = lazyNamed(() => import('./product-attribute-definitions'), 'AttributeDefinitionList');
const AttributeDefinitionCreate = lazyNamed(() => import('./product-attribute-definitions'), 'AttributeDefinitionCreate');
const AttributeDefinitionEdit = lazyNamed(() => import('./product-attribute-definitions'), 'AttributeDefinitionEdit');
const ProductCompositionList = lazyNamed(() => import('./product-compositions'), 'ProductCompositionList');
const ProductCompositionCreate = lazyNamed(() => import('./product-compositions'), 'ProductCompositionCreate');
const ProductCompositionEdit = lazyNamed(() => import('./product-compositions'), 'ProductCompositionEdit');
const ProductCompositionShow = lazyNamed(() => import('./product-compositions'), 'ProductCompositionShow');
const ClientList = lazyNamed(() => import('./clients'), 'ClientList');
const ClientCreate = lazyNamed(() => import('./clients'), 'ClientCreate');
const ClientEdit = lazyNamed(() => import('./clients'), 'ClientEdit');
const ClientShow = lazyNamed(() => import('./clients'), 'ClientShow');
const MobileOrderList = lazyNamed(() => import('./mobile-orders'), 'MobileOrderList');
const MobileOrderShow = lazyNamed(() => import('./mobile-orders'), 'MobileOrderShow');
const MobileOrderEdit = lazyNamed(() => import('./mobile-orders'), 'MobileOrderEdit');
const LoyaltyTransactionList = lazyNamed(() => import('./loyalty-transactions'), 'LoyaltyTransactionList');
const AdminActionList = lazyNamed(() => import('./admin-actions/AdminActionList'), 'AdminActionList');
const CampaignList = lazyNamed(() => import('./campaigns/CampaignList'), 'CampaignList');
const CampaignCreate = lazyNamed(() => import('./campaigns/CampaignCreate'), 'CampaignCreate');
const CampaignEdit = lazyNamed(() => import('./campaigns/CampaignEdit'), 'CampaignEdit');
const CampaignShow = lazyNamed(() => import('./campaigns/CampaignShow'), 'CampaignShow');
const EventList = lazyNamed(() => import('./events/EventList'), 'EventList');
const EventCreate = lazyNamed(() => import('./events/EventCreate'), 'EventCreate');
const EventEdit = lazyNamed(() => import('./events/EventEdit'), 'EventEdit');
const EventShow = lazyNamed(() => import('./events/EventShow'), 'EventShow');
const PromoCodeShow = lazy(() => import('./promo-codes/PromoCodeShow'));
const PromoCodeEdit = lazy(() => import('./promo-codes/PromoCodeEdit'));
const LoyaltyScanPage = lazyNamed(() => import('./loyalty'), 'LoyaltyScanPage');

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <Suspense fallback={<LazyFallback />}>{children}</Suspense>
);

// Permission keys gating each Resource. A user with no matching permission
// cannot navigate to the resource by URL — react-admin omits it from the
// route table when the function-children API filters it out.
const PERMS = {
    'telegram-products': 'telegram_products_admin',
    'promo-codes': 'promo_codes_admin',
    'campaigns': 'campaigns_admin',
    'events': 'events_admin',
    'product-compositions': 'product_compositions_admin',
    'products': 'products_admin',
    'admin/catalog-labels': 'product_compositions_admin',
    'admin/customization-options': 'customization_options_admin',
    'attribute-definitions': 'manage_attribute_definitions',
    'categories': 'categories_admin',
    'stock-movements': 'stock_movements_admin',
    'manual-orders': 'orders_admin',
    'clients': 'clients_admin',
    'mobile-orders': 'orders_admin',
    'loyalty-transactions': 'clients_admin',
    'users': 'users_management_admin',
    'roles': 'roles_management_admin',
    'permissions': 'roles_management_admin',
    'branches': 'branches_admin',
    'delivery-time-slots': 'delivery_slots_admin',
    'admin-actions': 'audit_log_admin',
} as const;

const can = (permissions: unknown, perm: string): boolean => {
    if (!Array.isArray(permissions)) return false;
    return permissions.includes(perm);
};

const App = () => (
    <ErrorBoundary>
        <Suspense fallback={<LazyFallback />}>
            <Admin
                dataProvider={dataProvider}
                authProvider={authProvider}
                i18nProvider={i18nProvider}
                disableTelemetry
                theme={houseLightTheme}
                darkTheme={houseDarkTheme}
                layout={CustomLayout}
                dashboard={Dashboard}
            >
                {(permissions: unknown) => (
                  <>
                {can(permissions, PERMS['telegram-products']) && <Resource
                    name="telegram-products"
                    list={TelegramProductList}
                    show={TelegramProductShow}
                    edit={TelegramProductEdit}
                    create={TelegramProductCreate}
                    recordRepresentation={(r) => r.title}
                    options={{ label: "Telegram Products" }}
                    icon={TelegramIcon}
                />}
                {can(permissions, PERMS['promo-codes']) && <Resource name="promo-codes" list={PromoCodeList} show={PromoCodeShow} edit={PromoCodeEdit} create={PromoCodeCreate} icon={LocalOfferIcon} />}
                {can(permissions, PERMS['campaigns']) && <Resource
                    name="campaigns"
                    list={CampaignList}
                    show={CampaignShow}
                    create={CampaignCreate}
                    edit={CampaignEdit}
                    recordRepresentation={(r) => r.title}
                    options={{ label: "Campaigns" }}
                    icon={CampaignIcon}
                />}
                {can(permissions, PERMS['events']) && <Resource
                    name="events"
                    list={EventList}
                    show={EventShow}
                    create={EventCreate}
                    edit={EventEdit}
                    recordRepresentation={(r) => r.title}
                    options={{ label: "Events" }}
                    icon={CelebrationIcon}
                />}
                {can(permissions, PERMS['product-compositions']) && <Resource
                    name="product-compositions"
                    list={ProductCompositionList}
                    show={ProductCompositionShow}
                    create={ProductCompositionCreate}
                    edit={ProductCompositionEdit}
                    recordRepresentation={(r) => r.title}
                    options={{ label: "Catalog" }}
                    icon={LocalFloristIcon}
                />}
                {can(permissions, PERMS['products']) && <Resource name="products"
                    list={ProductList}
                    show={ProductShow}
                    create={ProductCreate}
                    edit={ProductEdit}
                    recordRepresentation={(r) => r.title}
                    options={{ label: "Inventory" }}
                    icon={InventoryIcon}
                />}
                {can(permissions, PERMS['admin/catalog-labels']) && <Resource
                    name="admin/catalog-labels"
                    list={CatalogLabelList}
                    create={CatalogLabelCreate}
                    edit={CatalogLabelEdit}
                    recordRepresentation={(r) => r.name}
                    options={{ label: "Labels" }}
                    icon={LabelIcon}
                />}
                {can(permissions, PERMS['admin/customization-options']) && <Resource
                    name="admin/customization-options"
                    list={CustomizationOptionList}
                    create={CustomizationOptionCreate}
                    edit={CustomizationOptionEdit}
                    recordRepresentation={(r) => r.name}
                    options={{ label: "Customization Options" }}
                    icon={TuneIcon}
                />}
                {can(permissions, PERMS['attribute-definitions']) && <Resource
                    name="attribute-definitions"
                    list={AttributeDefinitionList}
                    create={AttributeDefinitionCreate}
                    edit={AttributeDefinitionEdit}
                    recordRepresentation={(r) => r.name || r.slug}
                    options={{ label: "Product Properties" }}
                    icon={StyleIcon}
                />}
                {can(permissions, PERMS['categories']) && <Resource
                    name="categories"
                    list={CategoryList}
                    show={CategoryShow}
                    create={CategoryCreate}
                    edit={CategoryEdit}
                    recordRepresentation={(r) => r.name}
                    options={{ label: "Categories" }}
                    icon={CategoryIcon}
                />}
                {can(permissions, PERMS['stock-movements']) && <Resource
                    name="stock-movements"
                    list={StockMovementList}
                    create={StockMovementCreate}
                    icon={SwapVertIcon}
                />}
                {can(permissions, PERMS['manual-orders']) && <Resource
                    name="manual-orders"
                    list={OrderList}
                    show={OrderShow}
                    create={OrderCreate}
                    edit={OrderEdit}
                    options={{ label: "Orders" }}
                    icon={ShoppingCartIcon}
                />}
                {can(permissions, PERMS['clients']) && <Resource
                    name="clients"
                    list={ClientList}
                    show={ClientShow}
                    create={ClientCreate}
                    edit={ClientEdit}
                    recordRepresentation={(r) => r.name || r.phone || r.instagramHandle || r.telegramUsername || r.id}
                    options={{ label: "Clients" }}
                    icon={ContactsIcon}
                />}
                {can(permissions, PERMS['mobile-orders']) && <Resource
                    name="mobile-orders"
                    list={MobileOrderList}
                    show={MobileOrderShow}
                    edit={MobileOrderEdit}
                    options={{ label: "Mobile Orders" }}
                />}
                {can(permissions, PERMS['loyalty-transactions']) && <Resource
                    name="loyalty-transactions"
                    list={LoyaltyTransactionList}
                    options={{ label: "Loyalty Transactions" }}
                />}
                {can(permissions, PERMS['users']) && <Resource
                    name="users"
                    list={UserList}
                    show={UserShow}
                    create={UserCreate}
                    edit={UserEdit}
                    recordRepresentation={(r) => r.username}
                    options={{ label: "Users" }}
                    icon={PeopleIcon}
                />}
                {can(permissions, PERMS['roles']) && <Resource
                    name="roles"
                    list={RoleList}
                    show={RoleShow}
                    create={RoleCreate}
                    edit={RoleEdit}
                    recordRepresentation={(r) => r.name}
                    options={{ label: "Roles" }}
                    icon={SecurityIcon}
                />}
                {can(permissions, PERMS['permissions']) && <Resource
                    name="permissions"
                    list={PermissionList}
                    show={PermissionShow}
                    create={PermissionCreate}
                    edit={PermissionEdit}
                    recordRepresentation={(r) => r.name}
                    options={{ label: "Permissions" }}
                />}
                {can(permissions, PERMS['branches']) && <Resource
                    name="branches"
                    list={BranchList}
                    show={BranchShow}
                    create={BranchCreate}
                    edit={BranchEdit}
                    recordRepresentation={(r) => r.name}
                    options={{ label: "Branches" }}
                    icon={StoreIcon}
                />}
                {can(permissions, PERMS['delivery-time-slots']) && <Resource
                    name="delivery-time-slots"
                    list={DeliveryTimeSlotList}
                    show={DeliveryTimeSlotShow}
                    create={DeliveryTimeSlotCreate}
                    edit={DeliveryTimeSlotEdit}
                    recordRepresentation={(r) => r.label}
                    options={{ label: "Delivery Time Slots" }}
                    icon={AccessTimeIcon}
                />}
                {can(permissions, PERMS['admin-actions']) && <Resource
                    name="admin-actions"
                    list={AdminActionList}
                    options={{ label: "Audit Log" }}
                    icon={SecurityIcon}
                />}
                <CustomRoutes>
                    {can(permissions, 'migration_manager_admin') && <Route path="/migration-manager" element={<SuspenseWrapper><MigrationManager /></SuspenseWrapper>} />}
                    {can(permissions, 'roles_management_admin') && <Route path="/role-management" element={<SuspenseWrapper><RoleManagement /></SuspenseWrapper>} />}
                    {can(permissions, 'analytics_admin') && <Route path="/analytics" element={<SuspenseWrapper><Analytics /></SuspenseWrapper>} />}
                    {can(permissions, 'app_settings_admin') && <Route path="/settings" element={<SuspenseWrapper><SettingsPage /></SuspenseWrapper>} />}
                    {can(permissions, 'app_settings_admin') && <Route path="/bouquet-builder-settings" element={<SuspenseWrapper><BouquetBuilderSettingsPage /></SuspenseWrapper>} />}
                    {can(permissions, 'orders_admin') && <Route path="/loyalty/scan" element={<SuspenseWrapper><LoyaltyScanPage /></SuspenseWrapper>} />}
                </CustomRoutes>
                  </>
                )}
            </Admin>
        </Suspense>
    </ErrorBoundary>
);

export default App;
